import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { AttachIcon, FileIcon, XIcon } from '#/shared/ui/icons'

import type { MassMediaAttachment } from '../model/use-mass-media'

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,application/pdf'

interface AttachmentTileProps {
  attachment: MassMediaAttachment
  onRemove: (id: string) => void
}

function AttachmentTile({ attachment, onRemove }: AttachmentTileProps) {
  const { t } = useTranslation()
  const { status, isImage, previewUrl, ext } = attachment
  return (
    <div className="animate-pop-in relative size-20 shrink-0">
      <div
        className={cn(
          'size-full overflow-hidden rounded-[10px] border border-gray-200 bg-paper-avatar',
          status === 'error' && 'border-red-500',
        )}
      >
        {isImage && previewUrl ? (
          <img src={previewUrl} alt="" className="size-full object-cover" />
        ) : (
          <div className="flex size-full items-center justify-center text-gray-500">
            <FileIcon className="size-7" />
          </div>
        )}
        {status === 'uploading' ? (
          <div className="absolute inset-0 flex items-center justify-center bg-black/35">
            <span className="size-5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
          </div>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => onRemove(attachment.id)}
        aria-label={t('massSms.media.remove')}
        className="absolute top-1 left-1 flex size-4 cursor-pointer items-center justify-center rounded-full bg-black/45 text-white transition hover:bg-black/60"
      >
        <XIcon className="size-2.5" />
      </button>
      {status === 'error' ? (
        <span className="absolute right-1 bottom-1.5 left-1.5 truncate rounded bg-red-500 px-1 py-0.5 text-center text-xs leading-none text-white">
          {t('massSms.media.failed')}
        </span>
      ) : (
        ext && (
          <span className="absolute bottom-1.5 left-1.5 rounded border border-gray-200 bg-white px-1 py-0.5 text-xs leading-none text-gray-300 uppercase">
            {ext}
          </span>
        )
      )}
    </div>
  )
}

interface MassSmsAttachmentsProps {
  attachments: ReadonlyArray<MassMediaAttachment>
  onAdd: (files: FileList | null) => void
  onRemove: (id: string) => void
  disabled: boolean
}

/** "Add media" button plus a strip of staged MMS attachment previews. */
export function MassSmsAttachments({
  attachments,
  onAdd,
  onRemove,
  disabled,
}: MassSmsAttachmentsProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  return (
    <div className="flex flex-col gap-2.5 border-t border-gray-200 px-5 py-2.5">
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        className="flex w-fit cursor-pointer items-center gap-1.5 text-xs text-gray-500 transition-colors hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <AttachIcon className="size-3.5" />
        {t('massSms.media.add')}
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        className="hidden"
        onChange={(event) => {
          onAdd(event.target.files)
          event.target.value = ''
        }}
      />
      {attachments.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {attachments.map((attachment) => (
            <AttachmentTile
              key={attachment.id}
              attachment={attachment}
              onRemove={onRemove}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}
